/**
 * PATH: packages/kanban-host/src/allocation/MoveToBoardPicker.tsx
 * FILE_ID: KANBANHOST-MOVETOBOARDPICKER-001
 * PURPOSE: Inline "Move to another board" picker rendered INSIDE the
 *          AllocationChip dropdown. Lets an operator re-allocate an
 *          email from its current (possibly wrong) board to any other
 *          board they can write to. A board click DRILLS into that
 *          board's lane list (MoveToBoardLaneList) so the operator
 *          chooses the exact lane, instead of the email being dumped
 *          on the left-most lane.
 *
 * v002 (2026-06-10): board→lane drill-down + faster open.
 *   Operator report (Newmark): *"it doesn't have lanes to triage in
 *   this email … I don't have board sub-menus here to allocate it to
 *   there"* + *"it took a time to load boards"*. v001 listed boards
 *   only and allocated to the default lane on click. v002 (a) drills
 *   board → lane using the lanes already present in the board-list
 *   payload (zero extra round-trip), and (b) the parent prefetches
 *   the board list when the chip dropdown opens so this view is warm
 *   by the time the operator reaches it.
 * WHY:     Operator report 2026-05-29 (Mark Newman): the inline
 *          "Change lane" popover on a Newmark inbox row pointed at the
 *          WRONG "#384 — Hartley House" board (Newmark has duplicate
 *          job boards from the Jonas bridge), so every lane it listed
 *          was wrong for the board the operator actually works in —
 *          AND the popover only let them change the LANE, never the
 *          BOARD. The board picker existed only in the right-click
 *          "Allocate to Kanban" ContextMenu submenu
 *          (AllocateToKanbanSubMenu), which is a different surface and
 *          not reachable from the inline chip. This component brings
 *          board re-allocation to the chip so the wrong-board case is
 *          self-serviceable.
 *
 * FLOW:
 *   ┌──────────────────────┐   ┌───────────────────────┐   ┌─────────────────┐
 *   │ useAllocateToKanban  │ → │ flatten + filter by   │ → │ board rows →    │
 *   │ MenuData (listBoards)│   │ search query (pure)   │   │ onPick(b, lane) │
 *   └──────────────────────┘   └───────────────────────┘   └─────────────────┘
 *
 * NON-RESPONSIBILITIES:
 *   - Does NOT perform the allocate mutation. The parent
 *     (AllocationChip) owns `emailId` + the supersedes-learning gate,
 *     so it does the `useKanbanQuickAllocate.run(...)` call. This
 *     component is a pure picker: the operator drills board → lane and
 *     the chosen `(boardId, laneId)` is handed back via `onPick`.
 *
 * DEPENDENCIES:
 *   - ../index : useAllocateToKanbanMenuData, AllocateMenuBoard/Data,
 *                pickDefaultLaneId, KanbanApi
 * RELATED:
 *   - ./AllocationChip.tsx : parent / consumer
 *   - ./AllocateToKanbanSubMenu.tsx : the ContextMenu-based sibling
 *     picker (same data source, different render surface)
 */

import { useCallback, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react'

import { cn } from '@quotech/tenant-shared/lib/utils'

import type { CreateBoardInput, KanbanApi } from '@quotech/ui/kanban'
import { CreateBoardDialog } from '@quotech/ui/kanban'

import {
  pickDefaultLaneId,
  useAllocateToKanbanMenuData,
  type AllocateMenuBoard,
  type AllocateMenuData,
} from '../index'
import { MoveToBoardLaneList, sortLanesForPicker } from './MoveToBoardLaneList'
import { useKanbanBoardLanes } from './useKanbanBoardLanes'
import { useCreateBoardFromPicker } from './useCreateBoardFromPicker'

// ──────────────────────────────────────────────────────────
// Pure helpers (I → O) — unit-tested without rendering React
// ──────────────────────────────────────────────────────────

/**
 * Flatten the four allocate-menu buckets (home → pinned → recent →
 * all) into a single ordered, de-duplicated list, dropping the board
 * the email is ALREADY on (no point "moving" to the same board).
 *
 * Bucket order is preserved so the operator's surfaced favourites
 * (home/pinned/recent) lead the list, then the long tail. The buckets
 * are already mutually de-duplicated upstream by `buildMenuBuckets`,
 * but we guard with a `seen` set anyway so this stays correct even if
 * a caller passes overlapping buckets.
 */
export function flattenAllocatableBoards(
  data: Pick<AllocateMenuData, 'home' | 'pinned' | 'recent' | 'all'>,
  excludeBoardId: string | null,
): AllocateMenuBoard[] {
  const out: AllocateMenuBoard[] = []
  const seen = new Set<string>()
  const push = (b: AllocateMenuBoard | null | undefined) => {
    if (!b) return
    if (excludeBoardId && b.id === excludeBoardId) return
    if (seen.has(b.id)) return
    seen.add(b.id)
    out.push(b)
  }
  push(data.home)
  data.pinned.forEach(push)
  data.recent.forEach(push)
  data.all.forEach(push)
  return out
}

/**
 * Case-insensitive substring filter on board name. Empty / whitespace
 * query returns the list unchanged so the picker shows everything
 * before the operator types.
 */
export function filterBoardsByQuery(
  boards: AllocateMenuBoard[],
  query: string,
): AllocateMenuBoard[] {
  const q = query.trim().toLowerCase()
  if (!q) return boards
  return boards.filter((b) => b.name.toLowerCase().includes(q))
}

/**
 * Resolve the default (left-most non-archived) lane id for a board
 * straight from its (possibly empty) embedded `lanes[]` payload.
 * Returns null when the board has zero usable lanes.
 *
 * NOTE (PERF-2026-06-14-KANBAN): the board-list payload no longer
 * embeds per-board lanes, so the picker's *row-enabled* gate now uses
 * the server-computed `board.laneCount`, and the *drill-in* default
 * lane comes from `useKanbanBoardLanes(...).defaultLaneId` (a lazy
 * per-board fetch). This pure helper is retained as the canonical
 * "left-most non-archived lane" rule and stays unit-tested, so any
 * caller that already holds a full `lanes[]` array (e.g. tests or a
 * future warm-cache path) gets identical behaviour.
 */
export function resolveDefaultLaneForBoard(
  board: AllocateMenuBoard,
): string | null {
  return pickDefaultLaneId(board.board.lanes, {
    boardKind: board.board.kind,
  })
}

// ──────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────

export interface MoveToBoardPickerProps {
  api: KanbanApi
  orgId: number | null
  /** Board the email is currently allocated to — excluded from the
   *  list so "move" never offers the same board. */
  currentBoardId: string | null
  /** Mirrors the parent's mutation-in-flight flag so rows disable
   *  while an allocate is running. */
  busy: boolean
  /** Back to the lane list. */
  onBack: () => void
  /** Selected a target board + its default lane. Parent performs the
   *  actual allocate (it owns emailId + the supersedes gate). */
  onPick: (boardId: string, laneId: string) => void
}

export function MoveToBoardPicker({
  api,
  orgId,
  currentBoardId,
  busy,
  onBack,
  onPick,
}: MoveToBoardPickerProps) {
  const [query, setQuery] = useState('')
  // The board the operator drilled into. null = showing the board
  // list; non-null = showing that board's lane list so they can pick
  // the exact lane (operator report 2026-06-10).
  const [selected, setSelected] = useState<AllocateMenuBoard | null>(null)
  // Enabled-on-mount: the picker only mounts when the operator opens
  // the "Move to another board" view, so the listBoards() round-trip
  // is naturally lazy (we don't pay it for every chip on every row).
  // The parent (AllocationChip) prefetches the same ['kanban-boards']
  // query when the dropdown opens, so by the time this mounts the
  // cache is usually already warm.
  const menu = useAllocateToKanbanMenuData({ api, orgId })

  // PERF-2026-06-14-KANBAN — drill-in lanes are fetched lazily now that
  // the board-list payload no longer embeds per-board lanes. The hook
  // is gated on `selected` (enabled === Boolean(boardId)), so we only
  // pay the round-trip once the operator drills into a board, and it
  // reuses the shared ['kanban-lanes', id] cache so opening that board
  // next is warm. Called unconditionally (Rules of Hooks) — it stays
  // idle while `selected` is null.
  const drill = useKanbanBoardLanes({
    api,
    boardId: selected?.id ?? null,
    boardKind: selected?.board.kind ?? null,
  })

  // FEAT-2026-06-15-ALLOCATE-CREATE-BOARD — "+ New board" dialog state.
  const [createOpen, setCreateOpen] = useState(false)
  const { createBoard } = useCreateBoardFromPicker({ api })

  // Create a board, then drill straight into its lane list so the
  // operator can finish the move. Errors propagate for inline display.
  const handleCreateBoard = useCallback(
    async (input: CreateBoardInput): Promise<{ id: string }> => {
      const newBoard = await createBoard(input)
      setSelected(newBoard)
      return { id: newBoard.id }
    },
    [createBoard],
  )

  const boards = useMemo(
    () => flattenAllocatableBoards(menu, currentBoardId),
    [menu, currentBoardId],
  )
  const filtered = useMemo(
    () => filterBoardsByQuery(boards, query),
    [boards, query],
  )

  const showLoading = menu.isLoading && menu.totalCount === 0

  // Second level — the operator drilled into a board: show its lanes
  // (lazily fetched via useKanbanBoardLanes) so they pick the exact
  // lane. Back returns to the board list.
  if (selected) {
    return (
      <MoveToBoardLaneList
        boardName={selected.name}
        boardSwatch={selected.swatch}
        lanes={sortLanesForPicker(drill.lanes)}
        defaultLaneId={drill.defaultLaneId}
        isLoading={drill.isLoading}
        busy={busy}
        onBack={() => setSelected(null)}
        onPick={(laneId) => onPick(selected.id, laneId)}
      />
    )
  }

  return (
    <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 w-full px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-3 w-3" aria-hidden />
        <span>Move to another board</span>
      </button>

      <div className="px-1.5 pb-1">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/60"
            aria-hidden
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search boards…"
            autoFocus
            className="w-full rounded border border-border bg-background pl-6 pr-2 py-1 text-[11px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label="Search boards"
          />
        </div>
      </div>

      <div className="max-h-[220px] overflow-y-auto">
        {showLoading && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            Loading boards…
          </div>
        )}
        {menu.isError && (
          <div className="px-2 py-1 text-[11px] text-red-500">
            Could not load boards.
          </div>
        )}
        {!showLoading && !menu.isError && boards.length === 0 && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            No other boards available.
          </div>
        )}
        {!showLoading && !menu.isError && boards.length > 0 && filtered.length === 0 && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            No boards match “{query.trim()}”.
          </div>
        )}
        {filtered.map((board) => {
          // PERF-2026-06-14-KANBAN — gate the row on the server-computed
          // active lane count instead of resolving a lane id from the
          // (now-empty) embedded lanes array. The exact lane is chosen
          // on the drill-in screen, which fetches lanes lazily, so the
          // board list only needs to know "does this board have lanes?"
          const hasLanes = board.laneCount > 0
          const disabled = busy || !hasLanes
          return (
            <button
              key={board.id}
              type="button"
              disabled={disabled}
              onClick={() => hasLanes && setSelected(board)}
              title={
                hasLanes
                  ? `Choose a lane on ${board.name}`
                  : `${board.name} has no lanes — add one in Kanban first`
              }
              className={cn(
                'flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors',
                'hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: board.swatch || '#94a3b8' }}
                aria-hidden
              />
              <span className="flex-1 truncate">{board.name}</span>
              {hasLanes && (
                <ChevronRight
                  className="h-3 w-3 text-muted-foreground/50 shrink-0"
                  aria-hidden
                />
              )}
              {!hasLanes && (
                <span className="text-[9px] text-muted-foreground shrink-0">
                  no lanes
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Fixed footer — create a board to move this email into without
          leaving the picker (operator 2026-06-15). Needs an org id to
          scope the board, so it's hidden when orgId is unknown. */}
      {orgId != null && (
        <div className="border-t border-border mt-1 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => setCreateOpen(true)}
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1.5 text-[11px] font-medium text-left rounded transition-colors',
              'text-foreground hover:bg-accent disabled:opacity-50',
            )}
            data-testid="move-create-board"
          >
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
              <Plus className="h-3 w-3" aria-hidden />
            </span>
            <span className="flex-1 truncate">New board…</span>
          </button>
        </div>
      )}

      {orgId != null && (
        <CreateBoardDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          organisationId={orgId}
          onCreate={handleCreateBoard}
          loadTemplateCandidates={() =>
            api.listBoards(orgId != null ? { organisationId: orgId } : undefined)
          }
        />
      )}
    </div>
  )
}
